/**
 * JSONValidator.ts
 * Utility for safe JSON parsing with validation
 */
import { z } from 'zod';
import { logger } from './ProfessionalLogger';

export interface ValidationResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Safely parses JSON string with optional Zod schema validation
 */
export function safeJSONParse<T = unknown>(
  jsonString: string,
  schema?: z.ZodSchema<T>
): ValidationResult<T> {
  try {
    const parsed = JSON.parse(jsonString);

    if (schema) {
      const validated = schema.parse(parsed);
      return { success: true, data: validated };
    }

    return { success: true, data: parsed as T };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessage = error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', ');
      logger.error('JSON validation error:', errorMessage);
      return { success: false, error: `Validation error: ${errorMessage}` };
    }
    if (error instanceof SyntaxError) {
      logger.error('JSON parse error:', error.message);
      return { success: false, error: `Invalid JSON: ${error.message}` };
    }
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('JSON parse unknown error:', errorMessage);
    return { success: false, error: `Unknown error: ${errorMessage}` };
  }
}

/**
 * Safely gets item from localStorage with JSON parsing
 */
export function safeGetJSONFromStorage<T = unknown>(
  key: string,
  schema?: z.ZodSchema<T>
): ValidationResult<T> | null {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return null;
    }
    return safeJSONParse<T>(item, schema);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      logger.error('localStorage quota exceeded');
      return { success: false, error: 'Storage quota exceeded' };
    }
    logger.error('localStorage get error:', error);
    return {
      success: false,
      error: `Storage error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

/**
 * Safely sets item in localStorage with JSON stringification
 */
export function safeSetJSONToStorage<T>(
  key: string,
  value: T
): { success: boolean; error?: string } {
  try {
    const jsonString = JSON.stringify(value);
    localStorage.setItem(key, jsonString);
    return { success: true };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      logger.error('localStorage quota exceeded when setting:', key);
      return { success: false, error: 'Storage quota exceeded' };
    }
    logger.error('localStorage set error:', error);
    return {
      success: false,
      error: `Storage error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}
